import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import DataFlowV2Service from "src/modules/data-protection/apis/DataFlowV2Service";
import { inferWrapperAssetIdFromFlows } from "../helpers/flowCards";
import {
    findWrapperAssetId,
    normalizeId,
} from "../helpers/flowForm";
import { DATA_FLOW_ASSETS_QUERY_KEY } from "./useDataFlowAssets";
import { SOFTWARE_DATA_FLOWS_V2_QUERY_KEY } from "./useSoftwareDataFlowsV2";

const EMPTY_GROUPS = {
    receives_from: [],
    sends_to: [],
    relations: [],
};

const useWrapperAssetId = ({
    softwareId,
    detailsAssetId,
    softwareName,
} = {}) => {
    const fromDetails = normalizeId(detailsAssetId);

    const assetsQuery = useQuery({
        queryKey: [DATA_FLOW_ASSETS_QUERY_KEY, "software"],
        queryFn: async () => {
            const service = DataFlowV2Service.getInstance();
            const response = await service.getSoftwareAssets();

            return response?.data?.assets || response?.assets || [];
        },
        enabled: Boolean(softwareId) && fromDetails == null,
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    const flowsQuery = useQuery({
        queryKey: [SOFTWARE_DATA_FLOWS_V2_QUERY_KEY, softwareId],
        queryFn: async () => {
            const service = DataFlowV2Service.getInstance();
            const response = await service.getSoftwareDataFlows(softwareId);

            return response?.data || EMPTY_GROUPS;
        },
        enabled: Boolean(softwareId) && fromDetails == null,
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    const wrapperAssetId = useMemo(() => {
        if (fromDetails != null) {
            return fromDetails;
        }

        return (
            findWrapperAssetId(assetsQuery.data, softwareId, softwareName) ??
            inferWrapperAssetIdFromFlows(flowsQuery.data)
        );
    }, [
        assetsQuery.data,
        flowsQuery.data,
        fromDetails,
        softwareId,
        softwareName,
    ]);

    return {
        wrapperAssetId,
        isResolving:
            fromDetails == null &&
            Boolean(softwareId) &&
            (assetsQuery.isLoading ||
                assetsQuery.isFetching ||
                flowsQuery.isLoading ||
                flowsQuery.isFetching),
    };
};

export default useWrapperAssetId;
